import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLogAPI } from '../utils/api';
import {
  ClockIcon,
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatDateTime } from '../utils/formatDateTime';

const actionColors = {
  'user.login': 'bg-blue-100 text-blue-700',
  'alert.acknowledged': 'bg-amber-100 text-amber-700',
  'alert.resolved': 'bg-emerald-100 text-emerald-700',
  'camera.post': 'bg-purple-100 text-purple-700',
  'camera.put': 'bg-purple-100 text-purple-700',
  'camera.delete': 'bg-red-100 text-red-700',
};

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', userId: '' });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit-log', page, filters],
    queryFn: () => auditLogAPI.getLogs({ page, limit: 50, ...filters }),
  });

  const logs = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  const { data: actionsData } = useQuery({
    queryKey: ['audit-log-actions'],
    queryFn: () => auditLogAPI.getActions(),
    staleTime: 60000,
  });

  const actions = actionsData?.data?.data || [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-description">Track user activities and system events</p>
        </div>
        <button onClick={() => refetch()} className="btn btn-secondary flex items-center gap-2">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="glass-card p-4 mb-6 flex flex-wrap items-center gap-4">
        <FunnelIcon className="w-5 h-5 text-slate-400" />
        <select
          value={filters.action}
          onChange={(e) => { setFilters(f => ({ ...f, action: e.target.value })); setPage(1); }}
          className="input py-2 px-3 text-sm min-w-[180px]"
          aria-label="Filter by action"
        >
          <option value="">All Actions</option>
          {actions.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {/* Log table */}
      <div className="glass-card overflow-hidden">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>Entity</th>
              <th>IP Address</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5}>
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-full" />
                  </td>
                </tr>
              ))
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-slate-500">
                  <ClockIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No audit log entries found
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td className="whitespace-nowrap text-sm text-slate-600">
                    {formatDateTime(log.createdAt)}
                  </td>
                  <td>
                    <span className={clsx(
                      'px-2 py-1 rounded-md text-xs font-semibold',
                      actionColors[log.action] || 'bg-slate-100 text-slate-600'
                    )}>
                      {log.action}
                    </span>
                  </td>
                  <td className="text-sm text-slate-600">
                    {log.entityType && (
                      <span className="text-slate-400">{log.entityType}</span>
                    )}
                    {log.entityId && (
                      <span className="text-xs text-slate-400 ml-1">({log.entityId.substring(0, 8)}...)</span>
                    )}
                  </td>
                  <td className="text-sm text-slate-500 font-mono">{log.ipAddress || '-'}</td>
                  <td className="text-sm text-slate-500 max-w-xs truncate">
                    {log.details ? JSON.stringify(log.details).substring(0, 80) : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.pages} ({pagination.total} entries)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page >= pagination.pages}
                className="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
